import { useLogger } from 'common';

import { Status } from '../types/Status';

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expirationMs: number;
}

export interface StatusCacheData {
  waterQuality?: {
    status: Status;
    quality: string;
  };
  hvac?: {
    filterStatus: string;
    replacementTime: string;
    hvac: string;
    status: Status;
  };
  airQuality?: {
    indoorAQI: number;
    outdoorAQI: number;
    status: Status;
  };
  energy?: {
    savingsAmount: number;
    status: Status;
  };
}

class StatusCache {
  private readonly CACHE_KEY = 'heartbeat_card_status_cache';
  private readonly DEFAULT_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes
  private logger = useLogger('StatusCache');

  private getCache(): Record<string, CacheEntry<any>> {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) return {};

      const parsed = JSON.parse(cached);
      return parsed || {};
    } catch (error) {
      this.logger.error('Error reading from cache', { error });
      return {};
    }
  }

  private setCache(data: Record<string, CacheEntry<any>>): void {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(data));
    } catch (error) {
      this.logger.error('Error writing to cache', { error });
    }
  }

  private isExpired(entry: CacheEntry<any>): boolean {
    return Date.now() - entry.timestamp > entry.expirationMs;
  }

  get<K extends keyof StatusCacheData>(key: K): StatusCacheData[K] | null {
    const cache = this.getCache();
    const entry = cache[key as string] as CacheEntry<StatusCacheData[K]> | undefined;

    if (!entry) {
      this.logger.debug(`Cache miss for key: ${key}`);
      return null;
    }

    if (this.isExpired(entry)) {
      this.logger.debug(`Cache expired for key: ${key}`);
      this.remove(key);
      return null;
    }

    this.logger.debug(`Cache hit for key: ${key}`);
    return entry.data;
  }

  set<K extends keyof StatusCacheData>(
    key: K,
    data: StatusCacheData[K],
    expirationMs: number = this.DEFAULT_EXPIRATION_MS,
  ): void {
    const cache = this.getCache();
    const cacheEntry: CacheEntry<StatusCacheData[K]> = {
      data,
      timestamp: Date.now(),
      expirationMs,
    };

    cache[key as string] = cacheEntry;
    this.setCache(cache);
    this.logger.debug(`Cached data for key: ${key}`);
  }

  remove<K extends keyof StatusCacheData>(key: K): void {
    const cache = this.getCache();
    delete cache[key as string];
    this.setCache(cache);
    this.logger.debug(`Removed cache for key: ${key}`);
  }

  clear(): void {
    localStorage.removeItem(this.CACHE_KEY);
    this.logger.debug('Cleared all cache');
  }

  getAllCachedStatus(): Partial<StatusCacheData> {
    const cache = this.getCache();
    const result: Partial<StatusCacheData> = {};

    Object.entries(cache).forEach(([key, entry]) => {
      if (!this.isExpired(entry)) {
        result[key as keyof StatusCacheData] = entry.data;
      }
    });

    return result;
  }
}

export const statusCache = new StatusCache();
